import { useEffect, useRef } from "react";

export function PillScroller({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const startX = useRef(0);
  const startScrollLeft = useRef(0);
  const moved = useRef(false);

  const onPointerDown = (e: React.PointerEvent) => {
    if (!ref.current) return;
    dragging.current = true;
    moved.current = false;
    startX.current = e.clientX;
    startScrollLeft.current = ref.current.scrollLeft;
    ref.current.style.cursor = "grabbing";
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging.current || !ref.current) return;
    const dx = e.clientX - startX.current;
    if (Math.abs(dx) > 4) moved.current = true;
    ref.current.scrollLeft = startScrollLeft.current - dx;
  };

  const onPointerUp = () => {
    dragging.current = false;
    if (ref.current) ref.current.style.cursor = "grab";
  };

  const onClickCapture = (e: React.MouseEvent) => {
    if (moved.current) {
      e.stopPropagation();
      e.preventDefault();
      moved.current = false;
    }
  };

  // Desktop mouse support: a plain mouse only scrolls vertically and the scrollbar is hidden,
  // so an overflowing pill strip (e.g. a takeaway's category nav) is unreachable. Translate a
  // vertical wheel into horizontal scroll. Native listener with passive:false so preventDefault
  // actually stops the page scrolling while the cursor is over the strip. Trackpads already send
  // deltaX, so only hijack when the gesture is predominantly vertical.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (el.scrollWidth <= el.clientWidth) return; // nothing to scroll
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return; // already horizontal
      el.scrollLeft += e.deltaY;
      e.preventDefault();
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  return (
    <div
      ref={ref}
      className={`relative flex gap-2 overflow-x-auto pb-1 select-none ${className ?? ""}`}
      style={{ scrollbarWidth: "none", cursor: "grab" }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onClickCapture={onClickCapture}
    >
      {children}
    </div>
  );
}
